import { Component, computed, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';
import { Cartographie } from '../../../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { Aptitude } from '../../../processus-aptitude/models/aptitude.model';
import { IndiceRésultats } from '../../../processus-indice-resultats/models/indice-résultats';
import { IndicateurPerformance } from '../../models/indicateur-performance';
import { IndicateurPerformanceStore } from '../../store/indicateur-performance.store';
import { FormItemIndicateurComponent } from './form-item-indicateur/form-item-indicateur.component';

@Component({
  selector: 'app-form-indicateurs',
  imports: [ButtonAddNewComponent, FormItemIndicateurComponent],
  templateUrl: './form-indicateurs.component.html',
})
export class FormIndicateursComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #indicateurStore = inject(IndicateurPerformanceStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);

  readonly aptitudes = input.required<Aptitude[]>();
  readonly indicesResultats = computed(() =>
    this.aptitudes()
      .filter((x) => x.indicesResultats.length > 0)
      .map((x) => ({ nom: x.nom, id: x.id, items: x.indicesResultats })),
  );
  readonly indiceResultats = input.required<IndiceRésultats>();
  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();
  readonly estCreation = input.required<boolean>();
  readonly form = this.#fb.group({
    indicateurs: this.#fb.array([]),
  });

  constructor() {
    effect(() => {
      if (!this.estCreation()) {
        this.indiceResultats().indicateursPerformance.forEach((indicateur) => this.addIndicateur(indicateur));
      } else {
        this.addIndicateur();
      }
      this.#indicateurStore.charger(this.referentiel().id);
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addIndicateur(indicateur?: IndicateurPerformance) {
    const form = this.#fb.group({
      id: [indicateur?.id],
      indicateur: [indicateur, [Validators.required]],
      indices: [indicateur?.idsIndicesResultats ?? [this.indiceResultats().id], [Validators.required]],
      nomIndice: [''],
    });
    this.indicateursForm.push(form);
  }

  onSubmit() {
    const indicateursModifiés = this.indicateursForm.value
      .filter((x: { indicateur: IndicateurPerformance }) => !!x.indicateur.nom.trim() && x.indicateur.id !== undefined)
      .map((x: { indicateur: IndicateurPerformance; indices: string[]; id: string }) => ({
        nom: x.indicateur.nom,
        idsIndicesResultats: x.indices,
        id: x.indicateur.id,
        idReferentiel: this.referentiel().id,
      }));

    const indicateursNouveaux = this.indicateursForm.value
      .filter((x: { indicateur: IndicateurPerformance }) => !!x.indicateur.nom.trim() && x.indicateur.id == undefined)
      .map((x: { indicateur: IndicateurPerformance; indices: string[] }) => ({
        nom: x.indicateur.nom,
        idsIndicesResultats: x.indices,
      }));

    if (indicateursModifiés.length === 0 && indicateursNouveaux.length === 0) {
      this.indicateursForm.markAllAsTouched();
      return;
    }

    if (this.alreadyExists(indicateursModifiés)) return;

    if (indicateursModifiés.length > 0) {
      this.#indicateurStore.modifier({
        data: {
          indicateurs: indicateursModifiés,
        },
        next: () => this.#sidepanelService.close(),
      });
    }

    if (indicateursNouveaux.length > 0) {
      this.#indicateurStore.créer({
        data: {
          idReferentiel: this.referentiel().id,
          indicateurs: indicateursNouveaux,
        },
        next: () => this.#sidepanelService.close(),
      });
    }
  }

  alreadyExists(
    indicateurs: {
      nom: string;
      idsIndicesResultats: string[];
      id: string;
    }[],
  ) {
    let doublonsPrésents = false;
    this.indicateursForm.controls.forEach((control) => control.get('indicateur')?.setErrors(null));

    // Doublons entre les formGroup
    const idsEnDoublon = indicateurs.map((x) => x.id).filter((id, idx, ids) => ids.indexOf(id) !== idx);

    if (idsEnDoublon.length > 0) {
      this.indicateursForm.controls
        .filter((c) => idsEnDoublon.includes(c.value.indicateur.id))
        .forEach((c) => c.get('indicateur')?.setErrors({ notUnique: true }));
      doublonsPrésents = true;
    }

    // Doublons lorsque l'indicateur est déjà lié dans l'indice
    indicateurs.forEach((indicateur) => {
      const indicesEnDoublonTrouvés = indicateur.idsIndicesResultats.filter((id, idx, ids) => ids.indexOf(id) !== idx);
      if (indicesEnDoublonTrouvés.length > 0) {
        this.indicateursForm.controls
          .filter((c) => indicateur.id === c.value.indicateur.id)
          .forEach((c) => c.get('indicateur')?.setErrors({ notUnique: true }));
        doublonsPrésents = true;
      }
    });

    this.indicateursForm.markAllAsTouched();

    return doublonsPrésents;
  }

  get indicateursForm() {
    return this.form.get('indicateurs') as FormArray<FormGroup>;
  }
}
